import { Component, h, Prop, State, Listen, Element, Build, Watch } from '@stencil/core';
import { debounce } from '../../../utils/helpers/debounce';

/**
 * Dropdown menu component with configurable trigger based on me-button.
 *
 * @slot dropdown-menu - {required}, {multiple}, <dropdown-menu-type-1>, <me-button variant="ghost">
 * @slot default - {multiple}, default slot for menu items. Content will appear in the dropdown without needing a slot attribute.
 * @example
 *   <me-dropdown label="Menu">
 *     <dropdown-menu-type-1 slot="dropdown-menu">
 *       <me-button variant="ghost">Help center</me-button>
 *       <me-button variant="ghost">Blog</me-button>
 *       <me-button variant="ghost">FAQ</me-button>
 *     </dropdown-menu-type-1>
 *   </me-dropdown>
 */
@Component({
  tag: 'me-dropdown',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Dropdown {
  @Element() el!: HTMLElement;

  /**
   * The text label for the dropdown trigger button.
   * Displayed as the button text.
   */
  @Prop() label!: string;

  /**
   * Optional URL for the dropdown trigger.
   * When provided, the button renders as an <a> tag with this href.
   * When omitted, the button renders as a <span> element with button role.
   */
  @Prop() href?: string;

  /**
   * Whether the dropdown trigger is currently active.
   * @default false
   */
  @Prop() isActive?: boolean = false;

  /**
   * Additional class modifiers for styling.
   */
  @Prop() modifiers?: string;

  /**
   * Size of the dropdown button trigger.
   * @default md
   */
  @Prop() size?: 'sm' | 'md' = 'md';

  /**
   * Disables the dropdown button.
   * @default false
   */
  @Prop() disabled?: boolean = false;

  /**
   * Whether to display a loading indicator on the button.
   * @default false
   */
  @Prop() isLoading?: boolean = false;

  /**
   * Accessibility label for screen readers.
   */
  @Prop() ariaLabel?: string;

  /**
   * Viewport width (px) at/below which the dropdown behaves as "mobile":
   * opens on click (not hover) and skips the desktop outside-click handler.
   * Should match the containing navbar's mobile breakpoint. Navbars propagate
   * their own breakpoint to their dropdowns.
   * @default 768
   */
  @Prop({ attribute: 'mobile-breakpoint' }) mobileBreakpoint: number = 768;

  @State() isOpen: boolean = false;
  @State() isMobile: boolean = false;
  @State() private hasBeenOpened: boolean = false;

  /**
   * Ref to the button element for programmatic access.
   */
  private buttonRef: HTMLElement | null = null;

  /**
   * Timer for delayed close on mouseleave.
   * Allows the user to move the cursor from the trigger to the menu
   * without the dropdown closing (important for mega menus with a gap).
   */
  private closeTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Check if dropdown-menu slot has any assigned content.
   * Used to determine whether to use named slot or default slot.
   */
  private hasDropdownMenuSlot = false;

  /**
   * Lifecycle hook: Determines if the viewport is mobile before rendering
   * and sets up appropriate event listeners.
   */
  componentWillLoad() {
    if (!Build.isBrowser) {
      const dropdownMenuSlot = this.el.querySelector('[slot="dropdown-menu"]');
      this.hasDropdownMenuSlot = !!dropdownMenuSlot;
      return;
    }

    this.isMobile = window.innerWidth <= this.mobileBreakpoint;
    this.debouncedResize = debounce(this.handleResize.bind(this), 120);
    window.addEventListener('resize', this.debouncedResize);

    if (!this.isMobile) {
      document.addEventListener('click', this.handleDocumentClick);
    }

    const dropdownMenuSlot = this.el.querySelector('[slot="dropdown-menu"]');
    this.hasDropdownMenuSlot = !!dropdownMenuSlot;
  }

  /**
   * Lifecycle hook: Cleans up event listeners when component is removed.
   */
  disconnectedCallback() {
    if (!Build.isBrowser) return;
    if (this.debouncedResize) {
      window.removeEventListener('resize', this.debouncedResize);
    }
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
    document.removeEventListener('click', this.handleDocumentClick);
  }

  /**
   * Update mobile status on window resize and adjust event listeners accordingly.
   * Adds or removes document click handlers based on viewport size.
   */
  private handleResize() {
    if (!Build.isBrowser) return;
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth <= this.mobileBreakpoint;

    if (wasMobile && !this.isMobile) {
      document.addEventListener('click', this.handleDocumentClick);
    } else if (!wasMobile && this.isMobile) {
      document.removeEventListener('click', this.handleDocumentClick);
    }
  }

  /**
   * Re-evaluate mobile/desktop mode when the breakpoint changes (e.g. a navbar
   * propagates its own breakpoint after this dropdown has already mounted).
   */
  @Watch('mobileBreakpoint')
  onMobileBreakpointChange() {
    this.handleResize();
  }

  /**
   * Close dropdown when clicking outside (desktop only).
   * Prevents dropdown from staying open when clicking elsewhere.
   */
  private handleDocumentClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    // Don't close if clicking within this dropdown
    if (!this.isMobile && this.isOpen && !this.el.contains(target)) {
      this.isOpen = false;
      this.removeFocusFromHiddenMenu();
    }
  };

  /**
   * Shows the dropdown on mouse enter (desktop only).
   * Enables hover behavior for desktop devices.
   */
  handleMouseEnter() {
    if (!this.isMobile && !this.disabled) {
      // Cancel any pending close so re-entering keeps the menu open
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }
      this.hasBeenOpened = true;
      this.isOpen = true;
    }
  }

  /**
   * Toggles the dropdown visibility when clicking the button.
   * Handles focus management for accessibility.
   */
  toggleDropdown(event: Event) {
    if (this.disabled || (this.href && !this.isMobile)) return;

    event.preventDefault();
    event.stopPropagation();
    const nextIsOpen = !this.isOpen;

    // Mark as opened before first expansion to avoid initial display:none state.
    if (nextIsOpen) {
      this.hasBeenOpened = true;
    }

    this.isOpen = nextIsOpen;

    if (this.isOpen) {
      setTimeout(() => {
        const firstMenuItem = this.el.querySelector('.me-dropdown__menu [role="menuitem"]') as HTMLElement;
        if (firstMenuItem) firstMenuItem.focus();
      }, 100);
    } else {
      this.removeFocusFromHiddenMenu();
    }
  }

  /**
   * Closes the dropdown on mouse leave (desktop only).
   * Completes the hover behavior for desktop devices.
   */
  handleMouseLeave(event: MouseEvent) {
    if (this.isMobile) return;

    const relatedTarget = event.relatedTarget as HTMLElement;

    // Don't close if mouse is still within this dropdown element
    if (relatedTarget && this.el.contains(relatedTarget)) {
      return;
    }

    // Delay close so the user can move the cursor from the trigger
    // to the mega-menu panel without the dropdown snapping shut.
    this.closeTimeout = setTimeout(() => {
      this.closeTimeout = null;
      this.isOpen = false;
      this.removeFocusFromHiddenMenu();
    }, 150);
  }

  /**
   * Handle keyboard interactions for accessibility.
   * Supports keyboard navigation through the dropdown.
   */
  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent) {
    const target = event.target as HTMLElement;
    const isTrigger = target.closest('me-button') && this.buttonRef?.contains(target);

    if (event.key === 'Escape') {
      event.preventDefault();
      this.isOpen = false;

      if (this.buttonRef) this.buttonRef.focus();
      return;
    }

    if (event.key === 'ArrowDown' && isTrigger) {
      event.preventDefault();
      this.isOpen = true;
      this.hasBeenOpened = true;
      setTimeout(() => {
        const menuItems = Array.from(this.el.querySelectorAll('.me-dropdown__menu [role="menuitem"]')) as HTMLElement[];
        const firstVisible = menuItems.find(el => el.offsetParent !== null && !el.hasAttribute('disabled'));
        if (firstVisible) {
          let focusTarget: HTMLElement | null = null;
          if (firstVisible.shadowRoot) {
            focusTarget = firstVisible.shadowRoot.querySelector('a,button,[tabindex]:not([tabindex="-1"])');
          }
          if (!focusTarget) {
            focusTarget = firstVisible.querySelector('a,button,[tabindex]:not([tabindex="-1"])');
          }
          if (focusTarget) {
            focusTarget.focus();
          } else {
            firstVisible.focus();
          }
        }
      }, 150);
      return;
    }

    if (event.key === ' ' && isTrigger) {
      event.preventDefault();
      this.isOpen = !this.isOpen;

      if (this.isOpen) {
        setTimeout(() => {
          const firstMenuItem = this.el.querySelector('.me-dropdown__menu [role="menuitem"]') as HTMLElement;
          if (firstMenuItem) firstMenuItem.focus();
        }, 100);
        if (this.isOpen) {
          this.hasBeenOpened = true;
        }
      }
      return;
    }

    if (event.key === 'Enter' && isTrigger) {
      if (!this.href) {
        event.preventDefault();
        const nextIsOpen = !this.isOpen;
        if (nextIsOpen) {
          this.hasBeenOpened = true;
        }
        this.isOpen = nextIsOpen;

        if (this.isOpen) {
          setTimeout(() => {
            const firstMenuItem = this.el.querySelector('.me-dropdown__menu [role="menuitem"]') as HTMLElement;
            if (firstMenuItem) firstMenuItem.focus();
          }, 100);
        }
      }

      return;
    }

    if (event.key === 'Tab' && Build.isBrowser && !this.isMobile) {
      setTimeout(() => {
        if (!this.el.contains(document.activeElement)) {
          this.isOpen = false;
        }
      }, 0);
    }
  }

  /**
   * Get button class string based on component state.
   * Handles active, open and other visual states.
   */
  private getButtonClasses(): string {
    const classes = ['me-dropdown__trigger'];

    if (this.isActive || this.isOpen) {
      classes.push('is-active');
    }

    return classes.filter(Boolean).join(' ');
  }

  /**
   * Handles the button ref capturing for focus management.
   */
  private setButtonRef = (el: HTMLElement | null) => {
    this.buttonRef = el;
  };

  private debouncedResize: () => void;

  /**
   * Remove focus from hidden menu if it was focused
   */
  private removeFocusFromHiddenMenu() {
    if (!Build.isBrowser) return;
    const menuEl = this.el.querySelector('.me-dropdown__menu') as HTMLElement | null;
    if (menuEl && menuEl.contains(document.activeElement)) {
      if (this.buttonRef) {
        this.buttonRef.focus();
      } else {
        (document.body as HTMLElement).focus();
      }
    }
  }

  render() {
    const dropdownClass = `me-dropdown ${this.modifiers || ''}`;
    const menuClass = {
      'me-dropdown__menu': true,
      'me-dropdown__menu--open': this.isOpen,
    };
    // Use inert attribute to exclude menu from focus when closed
    // But NOT on nested dropdowns' trigger buttons
    const inertAttr = !this.isOpen ? { inert: true } : {};

    const buttonProps: any = {
      'tag': this.href ? 'a' : 'span',
      'variant': 'ghost',
      'icon': 'arrows--chevron-down',
      'iconPosition': 'after',
      'iconPreload': true, // Force icon to load immediately (fixes nested dropdowns)
      'href': this.href,
      'class': this.getButtonClasses(),
      'size': this.size,
      'disabled': this.disabled,
      'clickHandler': event => this.toggleDropdown(event),
      'ariaLabel': this.ariaLabel || `${this.label} dropdown`,
      'aria-haspopup': 'menu',
      'aria-expanded': this.isOpen ? 'true' : 'false',
      'elRef': this.setButtonRef,
    };

    if (this.isLoading) {
      buttonProps.class = `${buttonProps.class} is-loading`;
    }

    if (this.isOpen) {
      setTimeout(() => {
        const menuItems = this.el.querySelectorAll('.me-dropdown__menu me-button');
        menuItems.forEach(el => {
          el.setAttribute('role', 'menuitem');
        });
      }, 0);
    }

    return (
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
      <div
        class={dropdownClass}
        role="group"
        onMouseEnter={() => this.handleMouseEnter()}
        onMouseLeave={e => this.handleMouseLeave(e)}
        onClick={e => {
          // Handle clicks on the trigger button (fallback for lifecycle issues)
          const button = this.buttonRef;
          if (button && (button === e.target || button.contains(e.target as Node))) {
            this.toggleDropdown(e);
          }
        }}
        onKeyDown={e => {
          // Handle keyboard events on the trigger button (fallback for lifecycle issues)
          const button = this.buttonRef;
          if (button && (button === e.target || button.contains(e.target as Node))) {
            this.toggleDropdown(e);
          }
        }}
      >
        <me-button {...buttonProps}>{this.label}</me-button>

        <div class={menuClass} role="menu" aria-hidden={!this.isOpen ? 'true' : 'false'} style={!this.hasBeenOpened ? { display: 'none' } : undefined} {...inertAttr}>
          <slot name="dropdown-menu"></slot>
          {!this.hasDropdownMenuSlot && <slot></slot>}
        </div>
      </div>
    );
  }
}
